import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Stack,
  Autocomplete,
  CircularProgress,
  Typography,
  Checkbox,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { countries } from "../countries";
import { styled } from "@mui/material/styles";
import Navbar2 from "./Navbar2";
import Footer from "./Footer";
import BusinessRegistrationDocuments from "./BusinessRegistrationDocuments";
import { useTranslation } from "react-i18next";
import {
  apiBaseUrl,
  apiVersion,
  businessUploadDocumentMakeAPI,
} from "../config-global";
import { toast } from "react-toastify";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

// ----------------------------------------------------------

const initialFormData = {
  name_en: "",
  name_ar: "",
  display_name: "",
  domain: "",
  website: "",
  vat: "",
  vat_image: null,
  cr: "",
  cr_image: null,
  admin_f_name: "",
  admin_l_name: "",
  admin_email: "",
  admin_country_code: "",
  admin_tel: "",
  services: [],
  is_onboarded: false,
};

const QontoConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    color: "theme.palette.green[500]",
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: "#005645",
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: "#005645",
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderTopWidth: 2,
    ...theme.applyStyles("dark", {
      borderColor: theme.palette.grey[800],
    }),
  },
}));

const RtlQontoConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    color: "theme.palette.green[500]",
    left: "calc(50% + 16px)",
    right: "calc(-50% + 16px)",
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: "#005645",
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: "#005645",
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderTopWidth: 2,
    ...theme.applyStyles("dark", {
      borderColor: theme.palette.grey[800],
    }),
  },
}));

export default function StepperForm() {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = React.useState("");
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const lang = i18n.language === "ar" ? "ar" : "en";
  const steps = [t("company_info"), t("documents"), t("admin_info")];
  const [categories, setCategories] = useState([]);
  const [vatPreview, setVatPreview] = useState({ url: null, type: null });
  const [crPreview, setCrPreview] = useState({ url: null, type: null });
  const [vatFileName, setVatFileName] = useState("");
  const [crFileName, setCrFileName] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [emailHelpText, setEmailHelpText] = useState("");
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  useEffect(() => {
    async function fetchData() {
      const response = await fetch(
        `${apiBaseUrl}${apiVersion}public/cache/services/all`
      );
      const data = await response.json();
      console.log(data.data);
      setCategories(data.data);
    }
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "admin_email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        setIsEmailValid(false);
        setEmailHelpText(t("Email is not valid"));
      } else {
        setIsEmailValid(true);
        setEmailHelpText("");
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const resetForm = () => {
    setFormData(initialFormData); // Reset form data
    setVatPreview({ url: null, type: null }); // Reset previews
    setCrPreview({ url: null, type: null }); // Reset previews
    setVatFileName(""); // Reset file names
    setCrFileName(""); // Reset file names
    setValue(null); // resets the selected country
    setInputValue(""); // <-- reset input value in Autocomplete
  };

  const handleNext = async () => {
    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
    } else {
      // Submit
      console.log("Submitted:", formData);
      const { cr_image, vat_image, ...business_data } = formData;
      console.log("Business Data:", business_data);

      setLoading(true);

      try {
        const response = await fetch(
          `${apiBaseUrl}${apiVersion}business/auth/signup`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(business_data),
          }
        );
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message || "Something went wrong");
        }

        const biz_id = data.data.biz_id;

        const documentData = new FormData();
        documentData.append("businessId", biz_id);
        if (formData.vat_image) {
          documentData.append(
            "vat_proof",
            formData.vat_image,
            formData.vat_image.name
          );
        }
        if (formData.cr_image) {
          documentData.append(
            "cr_proof",
            formData.cr_image,
            formData.cr_image.name
          );
        }

        const response2 = await fetch(`${businessUploadDocumentMakeAPI}`, {
          method: "POST",
          body: documentData,
        });

        if (!response2.ok) {
          throw new Error(
            "Something went wrong while uploading documents"
          );
        }

        toast.success(
          "Business registered and document uploaded successfully",
          {
            position: "top-center",
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
          }
        );

        setTimeout(() => {
          window.location.href = "/home";
        }, 2000);
      } catch (error) {
        toast.error(error.message || "Something went wrong", {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
      resetForm();
      setActiveStep(0); // Reset to the first step
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleAutocompleteChange = (event, newValue) => {
    setValue(newValue);
    setFormData({
      ...formData,
      admin_country_code: newValue ? `+${newValue.phone}` : "",
    });
    setInputValue(newValue ? `+${newValue.phone}` : "");
  };

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
  };

  const isStepValid = () => {
    switch (activeStep) {
      case 0:
        return (
          formData.name_en.trim() &&
          formData.name_ar.trim() &&
          formData.display_name.trim()
        );
      case 1:
        return (
          formData.vat_image &&
          formData.cr_image &&
          formData.vat.trim() &&
          formData.cr.trim()
        );
      case 2:
        return (
          formData.admin_f_name.trim() &&
          formData.admin_l_name.trim() &&
          formData.admin_email.trim() &&
          formData.admin_country_code &&
          formData.admin_tel.trim() &&
          formData.services.length > 0
        );
      default:
        return false;
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <>
            <TextField
              fullWidth
              label={t("name_en")}
              name="name_en"
              value={formData.name_en}
              onChange={handleChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label={t("name_ar")}
              name="name_ar"
              value={formData.name_ar}
              onChange={handleChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label={t("display_name_vat")}
              name="display_name"
              value={formData.display_name}
              onChange={handleChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label={t("company_domain")}
              name="domain"
              value={formData.domain}
              onChange={handleChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label={t("company_website")}
              name="website"
              value={formData.website}
              onChange={handleChange}
              margin="normal"
            />
          </>
        );
      case 1:
        return (
          <BusinessRegistrationDocuments
            formData={formData}
            setFormData={setFormData}
            vatPreview={vatPreview}
            setVatPreview={setVatPreview}
            crPreview={crPreview}
            setCrPreview={setCrPreview}
            vatFileName={vatFileName}
            setVatFileName={setVatFileName}
            crFileName={crFileName}
            setCrFileName={setCrFileName}
          />
        );
      case 2:
        return (
          <>
            <TextField
              fullWidth
              label={t("admin_fname")}
              name="admin_f_name"
              value={formData.admin_f_name}
              onChange={handleChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label={t("admin_lname")}
              name="admin_l_name"
              value={formData.admin_l_name}
              onChange={handleChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label={t("admin_email")}
              name="admin_email"
              value={formData.admin_email}
              onChange={handleChange}
              margin="normal"
              required
              error={!isEmailValid}
              helperText={emailHelpText}
            />
            <Stack direction="row" sx={{ mt: 2, mb: 2 }}>
              <Autocomplete
                id="country-select"
                options={countries}
                value={value}
                inputValue={inputValue}
                getOptionLabel={(option) =>
                  value && value.phone === option.phone
                    ? `+${option.phone}`
                    : option.label
                }
                renderOption={(props, option) => (
                  <li {...props}>
                    {option.label} (+{option.phone})
                  </li>
                )}
                onChange={handleAutocompleteChange}
                onInputChange={handleInputChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("country_code")}
                    variant="outlined"
                    // name="admin_country_code"
                    // value={formData.admin_country_code}
                    required
                  />
                )}
                sx={{ flex: 1 }}
              />

              <TextField
                type="number"
                name="admin_tel"
                label={t("telephone")}
                value={formData.admin_tel}
                onChange={handleChange}
                required
                sx={{ flex: 2, ...(lang === "en" ? { ml: 2 } : { mr: 2 }) }}
              />
            </Stack>
            <Autocomplete
              multiple
              id="select-services"
              fullWidth
              options={categories}
              value={categories.filter((cat) =>
                formData.services.includes(cat.srid)
              )}
              disableCloseOnSelect
              getOptionLabel={(option) => option.name + "-" + option.name_ar}
              isOptionEqualToValue={(option, value) => option.srid === value.srid}
              onChange={(event, selectedValues) => {
                setFormData((prev) => ({
                  ...prev,
                  services: selectedValues.map((val) => val.srid),
                }));
              }}
              renderOption={(props, option, { selected }) => {
                const { key, ...optionProps } = props;
                return (
                  <li key={key} {...optionProps}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.name + "-" + option.name_ar}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("select_services")}
                  placeholder="Services"
                  fullWidth
                  required
                />
              )}
            />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Navbar2 />
      <section className="contact-wrapper">
        <div className="faq-image">
          <div className="image-overlay"></div>
          <div className="faq-section-one">
            <h1>{t("business_registration")}</h1>

            <p>
              <a
                href="/contact"
                style={{ color: "#fff", textDecoration: "underline" }}
              >
                {t("contact_text")}
              </a>
            </p>
          </div>
        </div>
        <Box className="arounder-registration-card">
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            connector={
              lang === "en" ? <QontoConnector /> : <RtlQontoConnector />
            }
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <Box sx={{ mt: 4 }}>
            {renderStepContent(activeStep)}
            <Box display="flex" alignItems="center" gap={1} mt={3}>
              <InfoOutlinedIcon fontSize="small" color="action" />
              <Typography variant="caption" color="textSecondary">
                {t("marked_fields_are_mandatory")}
              </Typography>
            </Box>
            <Box
              sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}
            >
              <Button
                disabled={activeStep === 0}
                variant="contained"
                onClick={handleBack}
                className="back-btn"
                sx={{
                  padding: "1rem 2rem",
                  borderRadius: "1.25rem",
                  color: "#fff",
                  fontSize: "14px",
                  fontWeight: "600",
                  boxShadow: "none",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#05695f",
                  },
                }}
              >
                {t("back")}
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={loading || !isStepValid()}
                className="continue-btn"
                sx={{
                  padding: "1rem 2rem",
                  borderRadius: "1.25rem",
                  color: "#fff",
                  fontSize: "14px",
                  fontWeight: "600",
                  boxShadow: "none",
                  backgroundColor: "#00ccbb",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#00a99d",
                  },
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ color: "#fff" }} />{" "}
                    {t("submitting")}
                  </>
                ) : activeStep === steps.length - 1 ? (
                  t("submit")
                ) : (
                  t("continue")
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </section>
      <Footer />
    </>
  );
}
